import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, Observable, of, switchMap } from 'rxjs';

import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { ModalFactoryService } from '../../../global/services/modal-factory.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { ComponentRegistryService } from '../../../core/grid/services/component-registry.service';
import {
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { EfileAdminService } from '../../adminservices/efile-admin.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { SubmissionService } from '../../submission-data/submission.service';
import { EFileService } from '../../efile.service';
import { EfileWorkflowService, ReturnReviewStep, SaveCommentPayload } from '../return-review/efile-workflow.service';

import { EfileScrubbingActionsService } from './efile-scrubbing-actions.service';
import {
  CeHostDialogComponent,
  CeHostDialogData,
  ModalLockPayload,
} from '../../../shared/components/ce-host-dialog/ce-host-dialog.component';
import {
  buildEfileCeHostDialogData,
  mergeXmlVsElementsIrsIntoFilterParams,
} from '../../shared/efile-ce-dialog-context';

/**
 * Matches the payload shape that {@link GridDirective}.ggCallBack emits
 * (gizmoGrid invokes `options.callBack({callName, data, type})`). `data`
 * is typically `Array<{ data: <row> }>`.
 */
export interface EfileCtrlFnEvent {
  callName: string;
  data: any;
  type?: string;
  /**
   * Optional context for the run/history `searchKeyToReplaceKey` ctrlFn.
   * Hosts populate it from their filter bar before forwarding the
   * gridCallback through the dispatcher.
   */
  searchReplaceContext?: SearchReplaceContext;
  /**
   * Active step's WORKFLOW_STEP_ID. Required by the per-row comment
   * ctrlFns (`saveEfileDiagComment`, `savePyCyComment`) which must
   * persist the step id alongside the comment payload (legacy
   * `EfileFactory.saveComment` reads it from
   * `efileFactory.returnReviewSteps[...].find(s => s.STATE_URL ===
   * $state.current.name).WORKFLOW_STEP_ID`). Hosts forward the active
   * step's id when wiring `(gridCallback)`.
   */
  workflowStepId?: string | number;
  /**
   * Active legacy state URL leaf (e.g. `efile-diagnostics`). Required by
   * the diagnostics drilldown fallback so the dispatcher only routes
   * unrecognized callNames into `WorkspaceService.loadLink` for the
   * diagnostics step (legacy efile.js L668-672).
   */
  stateLeaf?: string;
}

/**
 * Snapshot of filter state required to execute a `searchKeyToReplaceKey`
 * save (efile.js L478-487). `searchKeys`/`replaceKeys` are CSVs of the
 * selected scrubbing entries; `formKeys` is only set on the run tab.
 */
export interface SearchReplaceContext {
  searchKeys: string;
  replaceKeys: string;
  formKeys?: string;
}

export type DispatchResult = 'refresh' | 'noop';

/**
 * Plan object returned by {@link EfileCtrlFnDispatcherService.plan}. Keeps
 * the dispatcher UI-agnostic: the host component owns the
 * `<gtw-confirmation-modal>` rendering and simply calls `execute()` after
 * its modal resolves (or immediately when `confirmConfig` is undefined).
 */
export interface CtrlFnPlan {
  callName: string;
  /** When present, the host should render a confirmation modal with this config before invoking `execute()`. */
  confirmConfig?: Partial<ConfirmationModalConfig>;
  execute(): Observable<DispatchResult>;
}

/**
 * Angular 12 port of the legacy AngularJS `ctrlFn` callbacks from
 * `ui/app/components/efile/efile.js`:
 *  - `enableDisableScrubbing`    (state: `efileScrubbing.list`,             ~L253)
 *  - `sub-sno-disable`           (state: `return-review.efile-sno-dups`,    ~L708)
 *  - `sub-req-validations`       (state: `return-review.scrubbings`,        ~L939)
 *  - `xml2ElementsCompare` (and casing variants) — `/getScreenModal` + `CeHostDialogComponent`
 *    hosting `gtw-xml-vs-elements-ce` (XmlVsElementsComponent), not the host-only confirm modal.
 *
 * Hosts forward gizmoGrid `(gridCallback)` into {@link EfileCtrlFnDispatcherService.plan};
 * when `confirmConfig` is set, the host shows `<gtw-confirmation-modal>` (same role as
 * legacy `confirm-action.html` / `simpleConfirmationCtrl`). On success, hosts should
 * refresh like legacy `$rootScope.$emit('efile-custom-grid-refresh')`.
 *
 * See the **Legacy ↔ plan() mapping** comment on {@link EfileCtrlFnDispatcherService.plan}
 * for a per–call-name breakdown (which legacy paths used `$uibModal`, which had no modal,
 * and what is not ported here).
 */
/**
 * Submission status that legacy `EfileCustomGrid` (EfileWorkflowController.js
 * L172) treats as fully locked — every grid `ctrlFn` is short-circuited
 * with a "submitted in production" warning. We mirror the same guard at
 * the dispatcher level so every return-review action inherits it.
 */
const PRODUCTION_ACCEPTED_STATUS = 'Production Accepted';

/**
 * Set of callNames that should be allowed even when the submission is
 * Production Accepted. These are read-only helpers that don't mutate
 * server state (they just navigate). Currently empty — every legacy
 * action listed in `EfileCustomGrid`'s wrapper guard is mutating, and
 * the diagnostics drilldown also runs through legacy AlertService when
 * the submission is locked because `loadLink` re-enters the wrapper on
 * the destination screen. Kept as an explicit allow-list so future
 * read-only actions don't silently break.
 */
const ALWAYS_ALLOWED_CALL_NAMES = new Set<string>();

/** `/getScreenModal` modal_name — matches ComponentRegistryService + legacy ModalFactory. */
const XML_VS_ELEMENTS_SCREEN_MODAL_NAME = 'xml2ElementsCompare';

@Injectable({ providedIn: 'root' })
export class EfileCtrlFnDispatcherService {
  constructor(
    private actions: EfileScrubbingActionsService,
    private snackbar: GtwSnackbarService,
    private globalService: GlobalService,
    private appService: AppService,
    private submissionService: SubmissionService,
    private efileService: EFileService,
    private efileAdminService: EfileAdminService,
    private workflowService: EfileWorkflowService,
    private workspaceService: WorkspaceService,
    private router: Router,
    private modalFactory: ModalFactoryService,
    private dialog: MatDialog,
    private componentRegistry: ComponentRegistryService,
  ) {}

  /**
   * Returns a {@link CtrlFnPlan} for a grid `ctrlFn` callback, or `null` when the
   * callName is not handled (hosts may still implement their own behavior).
   *
   * @remarks
   * **Legacy ↔ `plan()` mapping** (what opened a modal in AngularJS vs what happens here):
   *
   * | Call name | Legacy (AngularJS) | This dispatcher |
   * |-----------|-------------------|-----------------|
   * | `validate` | `EfileElementsController.DataErrorsController` `$scope.ctrlFn === 'validate'` (L1390-1425) → `$uibModal` `confirm-action.html` (`windowClass: 'modal-warning'`) → `EfileFactory.validate(SUBMISSION_ID)` → AlertService 'Validation has started…'. | `confirmConfig` + `runEfileBatchAction`. Wording follows the L1390-1425 path (`message: 'Are you sure you want to validate?'`), which is the variant fired by the IRS validation grid pencil icon and the bulk toolbar shortcut after the L1390 guard. |
   * | `validateBatch`, `generateBatch`, `efileBatchPollAck`, `downloadBatchAcks` | `EfileElementsController` `$scope.ctrlFn` → `$uibModal` `confirm-action.html` + `confirmationCtrl` → `EfileFactory[_callName](subIds)` (see actionMap ~L355-389). | `confirmConfig` + `runEfileBatchAction`. `downloadBatchAcks` still triggers zip download from `ArrayBuffer`. |
   * | `submitBatch`, `enabledDisabledFormData` | Same `confirmationCtrl` / `EfileFactory` pattern in actionMap. | **Not implemented** — returns `null`; wire here if Angular grids emit these. |
   * | `enableDisableScrubbing` | `efile.js` scrubbing list `ctrlFn` (~L253): **no** confirm modal — immediate `JsonObjectFactory.saveJSON`. | **No** `confirmConfig` — `execute` calls `runEnableDisableScrubbing` immediately. |
   * | `searchKeyToReplaceKey` | Run/history states (~L457 / ~L559): `simpleConfirmationCtrl` then `doReplace` reading `EfileCustomGridService.filters[api_url]`. | `confirmConfig` + `runSearchKeyToReplaceKey`; host must pass `event.searchReplaceContext` (replaces reading grid service filters in JS). |
   * | `sub-sno-disable`, `sub-req-validations` | `efile.js` (~L708 / ~L939): `simpleConfirmationCtrl` then `EfileFactory.disableDuplicateSno` / `resolveScrubbingsForSub`. | `confirmConfig` + `runSubmissionClobAction`. |
   * | `xml2elementscompare` / `xmlvselementscompare` | Grid 6134: `ModalFactory.getModal('xml2ElementsCompare', _data)` then `$uibModal` with server template, or Angular `openXmlVsElementsViaGetScreenModal` → `getScreenModal` + {@link CeHostDialogComponent} + `gtw-xml-vs-elements-ce`. | Same as legacy modal data path: `getScreenModal` + CE dialog; `filterParams` passed to the CE includes `irs_id` from tab filters or from the clicked row (see `buildCeHostDialogPayload`). |
   * | `saveEfileDiagComment`, `savePyCyComment` | `efile.js` (~L645 / ~L883): no modal; guards + save + `AlertService`. | No `confirmConfig`; snackbars in `runSaveComment`. |
   * | (default) diagnostics | Unrecognized `callName` on diagnostics grid → row `DRILLDOWN` / `loadLink` (`efile.js` ~L668). | `diagnosticsDrilldownPlan` when `stateLeaf === 'efile-diagnostics'`. |
   *
   * **Other legacy paths (not this `plan()`):**
   * - `efileBatchSubmission` opens `batchSubmission.html` directly (`EfileElementsController` ~L275),
   *   not `confirmationCtrl`.
   * - Legacy `ctrlFn` blocks `generateBatch` when `ROLE < 2` (~L219); not duplicated here — add
   *   if Angular submission grids expose the same rule.
   * - The legacy submissions-list `confirmationCtrl` actionMap (~L355-360) used a longer
   *   wording for `validate` ("Do you want to proceed with the validation for this
   *   submission?"). We deliberately follow the more recent
   *   `DataErrorsController` copy at L1390-1425 ("Are you sure you want to validate?")
   *   for parity with the IRS validation grid — the success message is identical
   *   on both paths so any downstream behaviour is unchanged.
   */
  plan(event: EfileCtrlFnEvent | null | undefined): CtrlFnPlan | null {
    if (!event || !event.callName) return null;
    const rawCallName = String(event.callName).trim();
    const callName = rawCallName.toLowerCase();
    const rows = Array.isArray(event.data) ? event.data : [];

    // Production-Accepted guard — mirrors EfileCustomGrid's ctrlFn wrapper
    // (EfileWorkflowController.js L171-184). Block every mutating action
    // when the active submission is already submitted in production.
    if (this.isProductionAccepted() && !ALWAYS_ALLOWED_CALL_NAMES.has(callName)) {
      return {
        callName: rawCallName,
        execute: () => {
          this.snackbar.openSnackBar(
            'You cannot perform this action on a submission that is already submitted in production.',
            'gtw-snackbar--yellow',
          );
          return of<DispatchResult>('noop');
        },
      };
    }
    // Legacy single-row payload: gizmoGrid pencil icons pass `{rowData:{…}}`
    // instead of an array. We keep both shapes accessible because the
    // submissions-list actions below need either the single SUBMISSION_ID
    // (validate) or a CSV of them (validateBatch, generateBatch, …).
    //
    // `primaryRowData` follows the same defensive merge order
    // `XmlVsElementsComponent` uses: event payload first, falling through
    // to `firstGridRowObjectFromPayload(activeScreen)` so toolbar paths
    // that fire `ctrlFn` without a row selection still see the live grid
    // context the user is looking at. Callers that *require* row data
    // (e.g. `runSaveComment`, `diagnosticsDrilldownPlan`) still guard
    // explicitly — the live fallback only kicks in for hosts that didn't
    // forward a row, never for paths that never had one to begin with.
    const rowDataPayload =
      event.data && !Array.isArray(event.data) && event.data.rowData
        ? event.data.rowData
        : null;
    const primaryRowData = this.resolveRowDataWithFallback(event);
    const resolvedWorkflowStepId =
      event.workflowStepId ??
      primaryRowData?.WORKFLOW_STEP_ID ??
      primaryRowData?.workflow_step_id;

    switch (callName) {
      // -----------------------------------------------------------------------
      // Submissions-list `ctrlFn` actions — port of
      // `EfileElementsController.js` L215-263 + `confirmationCtrl` actionMap
      // (L354-435). The Return Error column pencil icon (`type === 'form'`)
      // fires `ctrlFn('validate', {rowData}, 'form')` → confirm-action modal →
      // EfileFactory.validate(SUBMISSION_ID). Bulk toolbar actions share the
      // same modal flow but target multiple rows.
      //
      // Return-review grids (e.g. `submission-errors` / IRS validations RID)
      // use the same `validate` callName via gizmoGrid →
      // `EfileReturnReviewComponent.onGridCallback` → this dispatcher.
      //
      // The `validate` wording / icon below mirrors the more recent
      // `DataErrorsController` flow (`EfileElementsController.js`
      // L1390-1425) — `windowClass: 'modal-warning'`, header "Confirm",
      // body "Are you sure you want to validate?", success
      // "Validation has started. This process could take several minutes
      // and you will be notified via email once completed.". The older
      // `confirmationCtrl` actionMap copy (~L355-360, "Do you want to
      // proceed with the validation for this submission?") is intentionally
      // not used; the success message is identical on both paths so the
      // downstream `EfileFactory.validate(SUBMISSION_ID)` call is unchanged.
      // -----------------------------------------------------------------------

      case 'validate':
        return {
          callName: rawCallName,
          confirmConfig: {
            // Header / body match `confirm-action.html` rendering with the
            // values set by `DataErrorsController` at L1396-L1397.
            title: 'Confirm',
            message: 'Are you sure you want to validate?',
            // `modal-warning` window class on the legacy modal renders an
            // amber/triangle accent — use the equivalent fa-warning glyph
            // here (the modal markup looks at `iconClass` for its leading
            // glyph, not at the legacy `windowClass`).
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runEfileBatchAction(
              this.collectSubmissionIds(rowDataPayload, rows),
              'validate',
              // Mirrors the AlertService.add('success', …) string at
              // EfileElementsController.js L1416 (and the actionMap success
              // copy at L358-359, which is identical).
              'Validation has started. This process could take several minutes and you will be notified via email once completed.',
            ),
        };

      case 'validatebatch':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm',
            message:
              'Do you want to proceed with the validation for the selected submissions?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runEfileBatchAction(
              this.collectSubmissionIds(rowDataPayload, rows),
              'validateBatch',
              'Successfully validated the selected submissions.',
            ),
        };

      case 'generatebatch':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm',
            message:
              'Do you want to proceed with the XML Generation for the selected submissions?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runEfileBatchAction(
              this.collectSubmissionIds(rowDataPayload, rows),
              'generateBatch',
              'Successfully generated XMLs for the selected submissions.',
            ),
        };

      case 'efilebatchpollack':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm',
            message:
              'Do you want to proceed to get the acknowledgements for the selected submissions?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runEfileBatchAction(
              this.collectSubmissionIds(rowDataPayload, rows),
              'efileBatchPollAck',
              'Successfully received acknowledgements from IRS for the selected submissions.',
            ),
        };

      case 'downloadbatchacks':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm',
            message:
              'Do you want to proceed to download the latest acknowledgements for the selected submissions?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runEfileBatchAction(
              this.collectSubmissionIds(rowDataPayload, rows),
              'downloadBatchAcks',
              'Successfully downloaded the latest acknowledgements for the selected submissions.',
            ),
        };

      case 'enabledisablescrubbing':
        return {
          callName: rawCallName,
          execute: () => this.runEnableDisableScrubbing(rows),
        };

      // -----------------------------------------------------------------------
      // Scrubbing run + history tabs — port of efile.js L457-498 / L556-597.
      // The run tab passes a Form filter selection in the event payload's
      // `searchReplaceContext`; the history tab omits it. We accept the
      // selection through the event so the dispatcher stays UI-agnostic
      // (the host already knows its filter state).
      // -----------------------------------------------------------------------
      case 'searchkeytoreplacekey':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm Action',
            message: 'Are you sure you want to replace all the elements shown below?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runSearchKeyToReplaceKey(
              (event as EfileCtrlFnEvent).searchReplaceContext,
            ),
        };

      case 'sub-sno-disable':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm Action',
            message:
              'Disabled Elements can be reverted back from Efile Overrides Screen. Are you sure you want to disable the selected Elements?',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runSubmissionClobAction(
              rows,
              'disableDuplicateSno',
              'Selected Elements were disabled Successfully',
            ),
        };

      case 'sub-req-validations':
        return {
          callName: rawCallName,
          confirmConfig: {
            title: 'Confirm Action',
            message:
              'Resolve all the Scrubbings for the selected Elements? NOTE: Only the Editable Elements will be updated from here. Rest can be updated using the Add/Edit Screen.',
            iconClass: 'fa fa-sign-out',
            iconColor: 'txt-color-orangeDark',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonClass: 'btn-danger',
          },
          execute: () =>
            this.runSubmissionClobAction(
              rows,
              'resolveScrubbingsForSub',
              'Selected Elements are updated Successfully',
            ),
        };

      // XML vs elements (return-review / RIDs) — legacy `ModalFactory.getModal('xml2ElementsCompare')`
      // then workspace modal with `gtw-xml-vs-elements-ce` (XmlVsElementsComponent).
      // Host `gtw-confirmation-modal` must not replace that flow: no `confirmConfig` here;
      // we POST `/getScreenModal` then open `CeHostDialogComponent` like `GridDirective`.
      case 'xml2elementscompare':
      case 'xmlvselementscompare': {
        const snapshot: EfileCtrlFnEvent = { ...event };
        return {
          callName: event.callName,
          execute: () => this.openXmlVsElementsViaGetScreenModal(snapshot),
        };
      }

      // -----------------------------------------------------------------------
      // Per-row comment saves on the return-review grids — port of
      // efile.js L639-664 (efile-diagnostics) and L876-902 (py-cy). Triggered
      // when the user edits the COMMENTS cell inline and tabs out;
      // gizmoGrid fires `ctrlFn('save…Comment', {rowData})` on commit.
      // No confirmation modal — legacy saves silently and shows a
      // single-line AlertService toast.
      // -----------------------------------------------------------------------
      case 'saveefilediagcomment':
        return {
          callName: rawCallName,
          execute: () =>
            this.runSaveComment(
              resolvedWorkflowStepId,
              primaryRowData,
              'diag',
              (row) => this.isDiagFailureCountZero(row),
              'There are no diagnostics failures here! Adding comment here is not allowed.',
              event.data,
            ),
        };

      case 'savepycycomment':
        return {
          callName: rawCallName,
          execute: () =>
            this.runSaveComment(
              resolvedWorkflowStepId,
              primaryRowData,
              'pycy',
              (row) => this.isPyCyCountsMatching(row),
              'The counts are matching! Adding comment here is not allowed.',
              event.data,
            ),
        };

      default:
        // Diagnostics drilldown fallback — efile.js L668-672. Any
        // unrecognized callName fired from the diagnostics grid is
        // treated as a row-click and routed through
        // `WorkspaceService.loadLink('drillDown', …)` using the
        // serialized `DRILLDOWN` column as activity/screen target.
        if (event.stateLeaf === 'efile-diagnostics' && primaryRowData) {
          return this.diagnosticsDrilldownPlan(rawCallName, primaryRowData);
        }
        return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private context() {
    const submission = this.submissionService.activeSubmission;
    const gp = this.globalService.global?.globalParams || {};
    const baseUrl = generalConfigFromGlobal(this.globalService).base_url || '/gtw';
    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    return { submission, gp, baseUrl, ssoId };
  }

  // ---------------------------------------------------------------------------
  // Live `rowData` / `filterParams` resolvers — port of the defensive
  // multi-source pattern used by `XmlVsElementsComponent`
  // (xml-vs-elements.component.ts `applyWorkspaceContext` + `resolveIrsId`).
  //
  // gizmoGrid `ctrlFn` events arrive with whatever shape the host can
  // synthesise — sometimes `{rowData}`, sometimes `Array<{data:…}>`,
  // sometimes neither (toolbar action with no row selection). The legacy
  // controllers transparently fell back to `workspaceFactory.activeScreen`
  // for tab-level filters and to the first grid row for row context. We
  // mirror that here so any caller (planner, batch action, save-comment,
  // diagnostics drilldown, xml-vs-elements compare) sees a consistent
  // best-effort view of the user's current selection.
  // ---------------------------------------------------------------------------

  /** Snapshot of the live workspace + global state used as a fallback for
   *  rowData / filterParams when the gizmoGrid event itself is empty. */
  private resolveLiveScreenContext(): {
    liveFilters: any;
    liveRow: any;
    globalParams: any;
  } {
    const active = this.workspaceService?.workspaceFactory?.activeScreen || {};
    const liveFilters = normalizeFilterParams(active);
    const liveRow = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(active));
    const globalParams = (this.globalService as any)?.global?.globalParams || {};
    return { liveFilters, liveRow, globalParams };
  }

  /**
   * Best-effort `rowData` resolution for a gizmoGrid `ctrlFn` event.
   * Priority (highest first), matching how `XmlVsElementsComponent` merges
   * `hostRowData` over `firstGridRowObjectFromPayload(activeScreen)`:
   *   1. `event.data.rowData` (legacy single-row pencil shape)
   *   2. `event.data[0].data` / `event.data[0]` (bulk toolbar shape)
   *   3. live first grid row from `WorkspaceService.activeScreen.data`
   * Returns `null` when nothing is available — callers that *require*
   * rowData should still guard.
   */
  private resolveRowDataWithFallback(event: EfileCtrlFnEvent): any | null {
    const rowDataPayload =
      event?.data && !Array.isArray(event.data) && event.data.rowData
        ? event.data.rowData
        : null;
    const rows = Array.isArray(event?.data) ? event.data : [];
    const fromEvent = rowDataPayload ?? rows[0]?.data ?? rows[0] ?? null;
    if (fromEvent && typeof fromEvent === 'object') {
      return fromEvent;
    }
    const { liveRow } = this.resolveLiveScreenContext();
    return liveRow && Object.keys(liveRow).length > 0 ? liveRow : null;
  }

  /**
   * Mirrors `XmlVsElementsComponent.resolveIrsId()` — scans every source
   * the legacy `XmlVsElementsController` flow could populate (active-screen
   * filters, clicked grid row, live activeScreen first row, GlobalService
   * globalParams) and returns the first non-empty value across the broad
   * key list (`irs_id`, `IRS_ID`, `irsId`, `irs_submission_id`,
   * `IRS_SUBMISSION_ID`, `submission_id`, `SUBMISSION_ID`,
   * `IRSSUBMISSION_ID`, `IRS_SUBMISSIONID`).
   *
   * `eventRowData` / `eventFilterParams` are the per-call event-scoped
   * candidates (e.g. the rowData passed to the dispatcher) and are checked
   * first, matching the host-overrides-screen merge order in the component.
   */
  private resolveIrsIdAcrossSources(
    eventRowData: any,
    eventFilterParams?: any,
  ): string {
    const { liveFilters, liveRow, globalParams } = this.resolveLiveScreenContext();
    const sources: any[] = [
      eventFilterParams,
      liveFilters,
      eventRowData,
      liveRow,
      globalParams,
    ];

    const irsKeys = [
      'irs_id',
      'IRS_ID',
      'irsId',
      'irs_submission_id',
      'IRS_SUBMISSION_ID',
      'submission_id',
      'SUBMISSION_ID',
      'IRSSUBMISSION_ID',
      'IRS_SUBMISSIONID',
    ];

    for (const src of sources) {
      if (!src || typeof src !== 'object') {
        continue;
      }
      for (const key of irsKeys) {
        const v = (src as any)[key];
        if (v != null) {
          const s = String(v).trim();
          if (s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
            return s;
          }
        }
      }
    }
    return '';
  }

  /**
   * Resolve a single `submission_id` for batch flows when the gizmoGrid
   * event row data is missing. Order:
   *   1. live screen filterParams (`EfileReturnReviewComponent` registers
   *      tab-level submission_id/irs_id via `syncTabFiltersToActiveScreen`)
   *   2. globalParams `submission_id`
   *   3. `SubmissionService.activeSubmission.SUBMISSION_ID`
   * Returns `''` so the caller can fall back to its own UI message.
   */
  private resolveSubmissionIdFromContext(): string {
    const { liveFilters, globalParams } = this.resolveLiveScreenContext();
    const candidates = [
      liveFilters?.submission_id,
      liveFilters?.SUBMISSION_ID,
      globalParams?.submission_id,
      globalParams?.SUBMISSION_ID,
      this.submissionService.activeSubmission?.SUBMISSION_ID,
    ];
    for (const c of candidates) {
      if (c == null) continue;
      const s = String(c).trim();
      if (s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
        return s;
      }
    }
    return '';
  }

  private isProductionAccepted(): boolean {
    const status = (this.submissionService.activeSubmission as any)?.SUBMISSION_STATUS;
    return status === PRODUCTION_ACCEPTED_STATUS;
  }

  /**
   * Ensures the PY/CY "counts matching" guard works for both numeric and
   * formatted/string payloads from inline grid edits.
   */
  private isPyCyCountsMatching(row: any): boolean {
    const currentRaw =
      row?.CURRENT_COUNT ?? row?.current_count ?? row?.CURRENTCOUNT ?? row?.currentCount;
    const previousRaw =
      row?.PREV_COUNT ?? row?.prev_count ?? row?.PREVCOUNT ?? row?.prevCount;

    const current = this.parseComparableNumber(currentRaw);
    const previous = this.parseComparableNumber(previousRaw);
    if (current !== null && previous !== null) {
      return current === previous;
    }
    if (currentRaw != null && previousRaw != null) {
      return String(currentRaw).trim() === String(previousRaw).trim();
    }
    return false;
  }

  /**
   * Diagnostics inline-comment guard:
   * reject comment save only when every reported failure count is zero.
   * A row that shows failures under any of the summary/grid count columns
   * (DIAG_FAILED_CNT, FAILCNT, …) must still be allowed to save a comment.
   */
  private isDiagFailureCountZero(row: any): boolean {
    const counts = this.diagFailureCounts(row);
    if (!counts.length) {
      return false;
    }
    return counts.every((count) => count === 0);
  }

  private diagFailureCounts(row: any): number[] {
    if (!row || typeof row !== 'object') {
      return [];
    }
    const keys = [
      'DIAG_FAILED_CNT',
      'diag_failed_cnt',
      'DIAGFAILEDCNT',
      'diagFailedCnt',
      'FAILCNT',
      'failcnt',
      'FAILED_CNT',
      'FAILED_COUNT',
      'ERROR_CNT',
      'ERROR_COUNT',
      'DIAG_ERROR_CNT',
    ];
    const counts: number[] = [];
    for (const key of keys) {
      if (row[key] == null || row[key] === '') {
        continue;
      }
      const parsed = this.parseDiagFailureCount(row[key]);
      if (parsed !== null) {
        counts.push(parsed);
      }
    }
    return counts;
  }

  /**
   * Count cells may be plain numbers or markup such as a drilldown link
   * around the Diagnostics Failed Count. A cell counts as a failure when
   * any number in it is non-zero, so a link whose text is the failed count
   * is not treated as zero just because another token in the markup is 0.
   */
  private parseDiagFailureCount(value: any): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    const stripped = String(value ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/,/g, '')
      .trim();
    if (!stripped) {
      return null;
    }
    const matches = stripped.match(/-?\d+(?:\.\d+)?/g);
    if (!matches?.length) {
      return null;
    }
    const numbers = matches.map((token) => Number(token)).filter((n) => Number.isFinite(n));
    if (!numbers.length) {
      return null;
    }
    const failure = numbers.find((n) => n !== 0);
    return failure !== undefined ? failure : 0;
  }

  private parseComparableNumber(value: any): number | null {
    if (value == null || value === '') {
      return null;
    }
    const normalized = String(value).replace(/,/g, '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * GridSaveFactory `type=input` entry point for the same comment saves
   * that {@link plan} handles as ctrlFns. gizmoGrid inline edits pass
   * `{ rowData, colData }` rather than a ctrlFn event, so we resolve the
   * workflow step from {@link EfileWorkflowService.activeWorkflowStepId}
   * (set by Return Review while a step tab is open) and then reuse
   * {@link runSaveComment}.
   *
   * Returns `{ skipped: true }` when the save is rejected or fails (so
   * GridSaveFactory does not treat it as a successful post-save), or
   * `{ callSuccess: '1', refreshGrid: true }` on success so the owning
   * grid reloads.
   */
  saveCommentFromGridInput(data: any, kind: 'diag' | 'pycy'): Observable<any> {
    const rowData =
      data?.rowData ||
      (!Array.isArray(data) ? data : null) ||
      data?.[0]?.data ||
      data?.[0] ||
      {};
    const workflowStepId = this.resolveWorkflowStepId(undefined, rowData);

    const rejectIf =
      kind === 'diag'
        ? (row: any) => this.isDiagFailureCountZero(row)
        : (row: any) => this.isPyCyCountsMatching(row);
    const rejectMessage =
      kind === 'diag'
        ? 'There are no diagnostics failures here! Adding comment here is not allowed.'
        : 'The counts are matching! Adding comment here is not allowed.';

    return this.runSaveComment(workflowStepId, rowData, kind, rejectIf, rejectMessage, data).pipe(
      map((result) =>
        result === 'refresh'
          ? { callSuccess: '1', refreshGrid: true }
          : { skipped: true },
      ),
    );
  }

  private resolveWorkflowStepId(explicit: string | number | undefined, rowData: any): string | number | '' {
    const { liveFilters, globalParams } = this.resolveLiveScreenContext();
    const resolved = this.firstNonEmpty(
      explicit,
      this.workflowService.activeWorkflowStepId,
      rowData?.WORKFLOW_STEP_ID,
      rowData?.workflow_step_id,
      rowData?.STEP_ID,
      liveFilters?.workflow_step_id,
      liveFilters?.WORKFLOW_STEP_ID,
      globalParams?.workflow_step_id,
      globalParams?.WORKFLOW_STEP_ID,
    );
    return resolved === '' ? '' : resolved;
  }

  /** First value that is present and not a blank / null token. Returns `''` when none match. */
  private firstNonEmpty(...values: any[]): any {
    for (const value of values) {
      if (value == null) {
        continue;
      }
      const text = String(value).trim();
      if (text !== '' && text.toLowerCase() !== 'null' && text.toLowerCase() !== 'undefined') {
        return typeof value === 'string' ? text : value;
      }
    }
    return '';
  }

  /**
   * Comment text from the cell the user just edited. gizmoGrid writes the
   * new value onto `rowData[col.map]`, which is not always `COMMENTS`.
   * An empty edit is kept so clearing a comment is not replaced by another field.
   */
  private resolveCommentText(rowData: any, gridPayload?: any): string {
    const mappedKey = gridPayload?.colData?.map ?? gridPayload?.colData?.data;
    if (mappedKey && rowData && Object.prototype.hasOwnProperty.call(rowData, mappedKey)) {
      return rowData[mappedKey] == null ? '' : String(rowData[mappedKey]);
    }
    const commentKeys = [
      'COMMENTS',
      'COMMENT',
      'comments',
      'comment',
      'DIAG_COMMENT',
      'DIAG_COMMENTS',
    ];
    if (rowData) {
      for (const key of commentKeys) {
        if (Object.prototype.hasOwnProperty.call(rowData, key) && rowData[key] != null) {
          return String(rowData[key]);
        }
      }
    }
    return '';
  }

  /**
   * Shared handler for `saveEfileDiagComment` (woz0og) and
   * `savePyCyComment` (uqttl8). Mirrors the legacy guard +
   * `EfileFactory.saveComment` flow (efile.js L645-662 and L883-900,
   * EfileService.js L712-731): the predicate gate runs first (matching
   * counts → reject with toast), then the comment is persisted and the
   * caller gets a `'refresh'` so the inline-edited cell re-renders with
   * the saved value.
   */
  private runSaveComment(
    workflowStepId: string | number | undefined,
    rowData: any,
    kind: 'diag' | 'pycy',
    rejectIf: (row: any) => boolean,
    rejectMessage: string,
    gridPayload?: any,
  ): Observable<DispatchResult> {
    if (!rowData) {
      // gizmoGrid only fires the comment ctrlFn with a `{rowData}` shape;
      // anything else is a host wiring bug.
      this.snackbar.openSnackBar(
        'Unable to save comment: missing row data.',
        'gtw-snackbar--red',
      );
      return of<DispatchResult>('noop');
    }

    if (rejectIf(rowData)) {
      this.snackbar.openSnackBar(rejectMessage, 'gtw-snackbar--yellow');
      return of<DispatchResult>('noop');
    }

    const { submission, gp, baseUrl, ssoId } = this.context();
    const { liveFilters } = this.resolveLiveScreenContext();
    const submissionId = this.firstNonEmpty(
      rowData?.SUBMISSION_ID,
      rowData?.submission_id,
      liveFilters?.submission_id,
      liveFilters?.SUBMISSION_ID,
      submission?.SUBMISSION_ID,
      gp?.submission_id,
      gp?.SUBMISSION_ID,
    );
    if (!submissionId) {
      this.snackbar.openSnackBar('No active submission selected', 'gtw-snackbar--red');
      return of<DispatchResult>('noop');
    }
    return this.resolveWorkflowStepId$(workflowStepId, rowData, kind, baseUrl, submissionId).pipe(
      switchMap((resolvedStepId) => {
        if (resolvedStepId == null || resolvedStepId === '') {
          // Without the step id the save can't be attributed to the right
          // workflow step — bail with a snackbar instead of silently
          // posting a malformed payload.
          this.snackbar.openSnackBar(
            'Unable to save comment: missing workflow step context.',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }
        return this.postResolvedComment(
          resolvedStepId,
          rowData,
          kind,
          gridPayload,
          submissionId,
          submission,
          gp,
          baseUrl,
          ssoId,
          liveFilters,
        );
      }),
    );
  }

  /**
   * Inline comment saves do not carry `WORKFLOW_STEP_ID` on the grid row.
   * Legacy `EfileFactory.saveComment` read it from the return-review step
   * whose state URL matched the open diagnostics / py-cy tab. When that
   * id was not published on `activeWorkflowStepId`, load the submission's
   * steps and use the matching leaf (`efile-diagnostics` or `py-cy`).
   */
  private readonly workflowStepsBySubmission = new Map<string, ReturnReviewStep[]>();

  private resolveWorkflowStepId$(
    explicit: string | number | undefined,
    rowData: any,
    kind: 'diag' | 'pycy',
    baseUrl: string,
    submissionId: string | number,
  ): Observable<string | number | ''> {
    const known = this.resolveWorkflowStepId(explicit, rowData);
    if (known !== '') {
      return of(known);
    }
    const cacheKey = String(submissionId);
    const cached = this.workflowStepsBySubmission.get(cacheKey);
    if (cached) {
      return of(this.matchWorkflowStepId(cached, kind));
    }
    return this.workflowService.loadSteps(baseUrl, submissionId, this.globalService).pipe(
      map((response) => {
        const steps = Array.isArray(response?.jsonObject) ? response.jsonObject : [];
        if (steps.length) {
          this.workflowStepsBySubmission.set(cacheKey, steps);
        }
        const id = this.matchWorkflowStepId(steps, kind);
        if (id !== '') {
          this.workflowService.activeWorkflowStepId = id;
        }
        return id;
      }),
      catchError(() => of<string | number | ''>('')),
    );
  }

  private matchWorkflowStepId(
    steps: ReturnReviewStep[],
    kind: 'diag' | 'pycy',
  ): string | number | '' {
    const leaf = kind === 'diag' ? 'efile-diagnostics' : 'py-cy';
    const match =
      steps.find((step) => this.stateUrlLeaf(step?.STATE_URL) === leaf) ||
      steps.find((step) => this.stepNameMatchesKind(step?.STEP_NAME, kind));
    const id = match?.WORKFLOW_STEP_ID;
    if (id == null) {
      return '';
    }
    const text = String(id).trim();
    if (text === '' || text.toLowerCase() === 'null' || text.toLowerCase() === 'undefined') {
      return '';
    }
    return id;
  }

  private stepNameMatchesKind(stepName: string | undefined, kind: 'diag' | 'pycy'): boolean {
    const name = String(stepName || '').toLowerCase();
    if (!name) {
      return false;
    }
    if (kind === 'pycy') {
      return name.includes('py/cy') || name.includes('py-cy') || name.includes('pycy');
    }
    return /e-?file/.test(name) && /diagnos/.test(name);
  }

  private stateUrlLeaf(stateUrl: string | undefined): string {
    const raw = String(stateUrl || '').trim().toLowerCase();
    if (!raw) {
      return '';
    }
    const cut = Math.max(raw.lastIndexOf('.'), raw.lastIndexOf('/'));
    return (cut >= 0 ? raw.slice(cut + 1) : raw).trim();
  }

  private postResolvedComment(
    resolvedStepId: string | number,
    rowData: any,
    kind: 'diag' | 'pycy',
    gridPayload: any,
    submissionId: string | number,
    submission: any,
    gp: any,
    baseUrl: string,
    ssoId: string,
    liveFilters: any,
  ): Observable<DispatchResult> {
    const payload: SaveCommentPayload = {
      submission_id: submissionId,
      sso_id: String(this.firstNonEmpty(ssoId, gp?.sso_id, rowData?.SSO_ID) ?? ''),
      workflow_step_id: resolvedStepId,
      tax_year: this.firstNonEmpty(
        submission?.TAX_YEAR,
        rowData?.TAX_YEAR,
        rowData?.tax_year,
        liveFilters?.tax_year,
        gp?.tax_year,
      ),
      scenario: this.firstNonEmpty(
        gp?.scenario,
        rowData?.SCENARIO,
        rowData?.scenario,
        liveFilters?.scenario,
      ),
      jcd_key: 250,
      comment: this.resolveCommentText(rowData, gridPayload),
    };
    if (kind === 'diag') {
      const diagName = this.firstNonEmpty(
        rowData?.DIAG_NAME,
        rowData?.diag_name,
        rowData?.DIAG_DESC,
        rowData?.DIAGNOSTIC_NAME,
      );
      if (diagName !== '') {
        payload.diag_name = String(diagName);
      }
    } else {
      const formName = this.firstNonEmpty(rowData?.FORM, rowData?.form);
      if (formName !== '') {
        payload.form = String(formName);
      }
    }

    return this.workflowService.saveComment(baseUrl, kind, payload).pipe(
      map((response: any) => {
        if (String(response?.callSuccess) === '0') {
          this.snackbar.openSnackBar(
            response?.errorMessage ||
              response?.message ||
              'Something went wrong while saving the data!',
            'gtw-snackbar--red',
          );
          return 'noop' as DispatchResult;
        }
        this.snackbar.openSnackBar('Comment saved successfully!', 'gtw-snackbar--green');
        return 'refresh' as DispatchResult;
      }),
      catchError((err: any) => {
        this.snackbar.openSnackBar(
          err?.error?.errorMessage ||
            err?.error?.message ||
            err?.message ||
            'Something went wrong while saving the data!',
          'gtw-snackbar--red',
        );
        return of<DispatchResult>('noop');
      }),
    );
  }

  /**
   * Diagnostics row-click drilldown — efile.js L668-672. Each row in
   * the diagnostics grid carries a serialized `DRILLDOWN` JSON blob
   * with the target screen's `activity_key` + `screen_key`; clicking
   * the row pushes the user into that workspace screen with the
   * canonical 6-key filter set so the drilldown lands pre-filtered.
   */
  private diagnosticsDrilldownPlan(callName: string, rowData: any): CtrlFnPlan | null {
    let drilldown: any;
    try {
      drilldown =
        typeof rowData.DRILLDOWN === 'string'
          ? JSON.parse(rowData.DRILLDOWN)
          : rowData.DRILLDOWN;
    } catch {
      drilldown = null;
    }
    if (!drilldown || !drilldown.activity_key || !drilldown.screen_key) {
      return null;
    }
    return {
      callName,
      execute: () => {
        const filterJson = [
          { filter_key: '1',    name: 'tax_year',         value: rowData.TAX_YEAR != null ? String(rowData.TAX_YEAR) : '' },
          { filter_key: '2305', name: 'submission_id',    value: rowData.SUBMISSION_ID },
          { filter_key: '24',   name: 'scenario',         value: rowData.SCENARIO },
          { filter_key: '1044', name: 'filter_by',        value: rowData.FILTER_KEY },
          { filter_key: '9',    name: 'parent_me_string', value: rowData.ME_KEYS },
          { filter_key: '1002', name: 'ho_leid_opt',      value: rowData.HO_LEID },
        ];
        try {
          this.workspaceService.loadLink(
            'drillDown',
            '{}',
            String(drilldown.activity_key),
            String(drilldown.screen_key),
            filterJson,
          );
          const active = this.workspaceService.workspaceFactory?.activeScreen;
          const slug = active ? this.workspaceService.getScreenSlug(active) : '';
          if (slug) {
            // Helper accounts for top-level legacy states that live
            // OUTSIDE `/workspace/` — see
            // WorkspaceService.getScreenNavigationCommands.
            const commands = this.workspaceService.getScreenNavigationCommands(active);
            const clientKey = sessionStorage.getItem('client_key') || '0';
            this.router.navigate(commands, {
              queryParams: { client_key: clientKey },
            });
          }
          return of<DispatchResult>('noop');
        } catch (err) {
          console.error('[EfileCtrlFnDispatcher] diagnostics drilldown failed', err);
          this.snackbar.openSnackBar(
            'Unable to open drilldown. Please try again.',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }
      },
    };
  }

  private runSearchKeyToReplaceKey(
    ctx: SearchReplaceContext | undefined,
  ): Observable<DispatchResult> {
    const { submission, gp, baseUrl, ssoId } = this.context();
    if (!submission?.SUBMISSION_ID) {
      this.snackbar.openSnackBar('No active submission selected', 'gtw-snackbar--red');
      return of<DispatchResult>('noop');
    }
    if (!ctx || !ctx.searchKeys || !ctx.replaceKeys) {
      // Mirrors the legacy filter `required: 1` contract — backend would
      // 400 anyway, but a snackbar is friendlier than a console error.
      this.snackbar.openSnackBar(
        'Select a Scrubbing entry from the filter before running replace.',
        'gtw-snackbar--yellow',
      );
      return of<DispatchResult>('noop');
    }

    return this.actions
      .searchKeyToReplaceKey(baseUrl, {
        submission_id: submission.SUBMISSION_ID,
        tax_year: submission.TAX_YEAR ?? gp.tax_year,
        scenario: gp.scenario,
        jcd_key: 250,
        sso_id: ssoId,
        search_key: ctx.searchKeys,
        replace_key: ctx.replaceKeys,
        formKey: ctx.formKeys,
      })
      .pipe(
        map((response: any) => {
          if (String(response?.callSuccess) === '0') {
            this.snackbar.openSnackBar(
              response?.errorMessage || 'Something went wrong while saving the data!',
              'gtw-snackbar--red',
            );
            return 'noop' as DispatchResult;
          }
          this.snackbar.openSnackBar('Data scrubbed successfully!', 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }),
        catchError(() => {
          this.snackbar.openSnackBar(
            'Something went wrong while saving the data!',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }),
      );
  }

  /**
   * IRS Submissions filter `irs_id` + GET `validateSubmissionXml` (legacy
   * {@link EfileFactory.xmlVsElementsCompare} / {@link XmlVsElementsComponent}).
   *
   * `irs_id` resolution mirrors `XmlVsElementsComponent.resolveIrsId()`
   * exactly: scan event filterParams, live screen filters, event row data,
   * live first grid row, and `globalParams` across the broad casing key
   * list (`irs_id` / `IRS_ID` / `irsId` / `irs_submission_id` /
   * `IRS_SUBMISSION_ID` / `submission_id` / `SUBMISSION_ID` /
   * `IRSSUBMISSION_ID` / `IRS_SUBMISSIONID`). When nothing resolves we
   * dump the inspected sources to the console (same diagnostic the
   * component prints) so wiring bugs are obvious in the field.
   */
  private runXmlVsElementsCompare(event?: EfileCtrlFnEvent): Observable<DispatchResult> {
    const eventRowData = event ? this.resolveRowDataWithFallback(event) : null;
    const eventFilterParams = (event as any)?.filterParams || null;

    // Run the row through `mergeXmlVsElementsIrsIntoFilterParams` first so
    // any row-shaped IRS id (the canonical `IRS_SUBMISSION_ID` shape on
    // grid rows) is normalised to `irs_id` before the broader scan.
    const mergedFp = mergeXmlVsElementsIrsIntoFilterParams(
      eventFilterParams || {},
      eventRowData || {},
    );
    const irsId = this.resolveIrsIdAcrossSources(eventRowData, mergedFp);

    if (!irsId) {
      const { liveFilters, liveRow, globalParams } = this.resolveLiveScreenContext();
      console.warn(
        '[EfileCtrlFnDispatcher] xml-vs-elements: irs_id not found in any source.',
        {
          eventFilterParams,
          eventRowData,
          liveActiveScreenFilters: liveFilters,
          liveActiveScreenRow: liveRow,
          globalParams,
        },
      );
      this.snackbar.openSnackBar(
        'Please select an irs submission to extract elements from.',
        'gtw-snackbar--yellow',
      );
      return of<DispatchResult>('noop');
    }

    const gc = generalConfigFromGlobal(this.globalService) as Record<string, any>;
    const efileApiUrl = String(
      gc.gtw_efile_api_url || gc.efile || gc.gtwEfileApiUrl || '',
    ).trim();
    if (!efileApiUrl) {
      this.snackbar.openSnackBar('Missing e-file API configuration.', 'gtw-snackbar--red');
      return of<DispatchResult>('noop');
    }

    return this.efileAdminService.xmlVsElementsCompare(efileApiUrl, irsId).pipe(
      map((res: any) => {
        const data = res?.data ?? res;
        if (String(data?.callSuccess) === '1') {
          this.snackbar.openSnackBar('XML Elements Extraction was Completed!', 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }
        this.snackbar.openSnackBar(
          data?.errorMessage && String(data.errorMessage) !== 'null'
            ? String(data.errorMessage)
            : 'Error',
          'gtw-snackbar--red',
        );
        return 'noop' as DispatchResult;
      }),
      catchError(() => {
        this.snackbar.openSnackBar('Error occurred.', 'gtw-snackbar--red');
        return of<DispatchResult>('noop');
      }),
    );
  }

  private runEnableDisableScrubbing(rows: any[]): Observable<DispatchResult> {
    const { submission, gp, baseUrl, ssoId } = this.context();
    if (!submission?.SUBMISSION_ID) {
      this.snackbar.openSnackBar('No active submission selected', 'gtw-snackbar--red');
      return of<DispatchResult>('noop');
    }

    const scrubbingIds = rows
      .map((r: any) => r?.data?.SCRUBBING_ID)
      .filter((id: any) => id !== undefined && id !== null)
      .join(',');

    return this.actions
      .enableDisableScrubbing(baseUrl, {
        submission_id: submission.SUBMISSION_ID,
        scrubbing_id: scrubbingIds,
        tax_year: submission.TAX_YEAR ?? gp.tax_year,
        scenario: gp.scenario,
        jcd_key: 250, // legacy pin, see efile.js L260
        sso_id: ssoId,
      })
      .pipe(
        map((response: any) => {
          if (String(response?.callSuccess) === '0') {
            this.snackbar.openSnackBar(
              response?.errorMessage || 'Something went wrong while saving the data!',
              'gtw-snackbar--red',
            );
            return 'noop' as DispatchResult;
          }
          this.snackbar.openSnackBar('Data saved successfully!', 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }),
        catchError(() => {
          this.snackbar.openSnackBar(
            'Something went wrong while saving the data!',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }),
      );
  }

  /**
   * Extract SUBMISSION_ID(s) the same way the legacy `confirmationCtrl`
   * did (`EfileElementsController.js` L447): single-row pencil paths carry
   * `rowData`, bulk toolbar actions carry an array of `{data:{…}}`.
   *
   * Defensive fallback (parity with `XmlVsElementsComponent` resolution
   * order — host overrides screen, screen falls back to globals): if the
   * gizmoGrid event payload has no submission ids we read the live
   * `activeScreen.filters.getFilterParams().submission_id` (registered by
   * `EfileReturnReviewComponent` for `xml-vs-elements*` tabs and by
   * `EfileDashboardController` parity stubs) and finally the active
   * submission from `SubmissionService`. That keeps validate / batch
   * actions working when fired from a toolbar without a row selection.
   */
  private collectSubmissionIds(rowData: any | null, rows: any[]): string {
    if (rowData?.SUBMISSION_ID != null) {
      return String(rowData.SUBMISSION_ID);
    }
    const fromRows = rows
      .map((r: any) => r?.data?.SUBMISSION_ID ?? r?.SUBMISSION_ID)
      .filter((id: any) => id !== undefined && id !== null && id !== '')
      .join(',');
    if (fromRows) {
      return fromRows;
    }
    return this.resolveSubmissionIdFromContext();
  }

  /**
   * Shared handler for the submissions-list confirm-action flows. Mirrors
   * the response branching in `EfileElementsController.js` L459-473:
   *   - `callSuccess === '1'`  → success snackbar + grid refresh.
   *   - `callSuccess === '2'`  → warning snackbar.
   *   - anything else          → generic error snackbar.
   * The `downloadBatchAcks` action returns an ArrayBuffer; we trigger the
   * zip download from the browser instead of branching on callSuccess.
   */
  private runEfileBatchAction(
    submissionIds: string,
    callName:
      | 'validate'
      | 'validateBatch'
      | 'generateBatch'
      | 'efileBatchPollAck'
      | 'downloadBatchAcks',
    successMessage: string,
  ): Observable<DispatchResult> {
    if (!submissionIds) {
      this.snackbar.openSnackBar(
        'No submissions selected for this action.',
        'gtw-snackbar--red',
      );
      return of<DispatchResult>('noop');
    }

    if (callName === 'downloadBatchAcks') {
      return this.efileService.downloadBatchAcks(submissionIds).pipe(
        map((buffer: ArrayBuffer) => {
          this.downloadZipAcks(buffer);
          this.snackbar.openSnackBar(successMessage, 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }),
        catchError(() => {
          this.snackbar.openSnackBar(
            'Failed to download acknowledgements. Please try again.',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }),
      );
    }

    const request$ =
      callName === 'validate'
        ? this.efileService.validateSubmission(submissionIds)
        : callName === 'validateBatch'
          ? this.efileService.validateBatch(submissionIds)
          : callName === 'generateBatch'
            ? this.efileService.generateBatch(submissionIds)
            : this.efileService.pollAckBatch(submissionIds);

    return request$.pipe(
      map((response: any) => {
        const code = String(response?.callSuccess ?? '');
        if (code === '1') {
          this.snackbar.openSnackBar(successMessage, 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }
        if (code === '2') {
          this.snackbar.openSnackBar(
            response?.errorMessage || 'Request completed with warnings.',
            'gtw-snackbar--yellow',
          );
          return 'noop' as DispatchResult;
        }
        this.snackbar.openSnackBar(
          'Something went wrong! Please contact support.',
          'gtw-snackbar--red',
        );
        return 'noop' as DispatchResult;
      }),
      catchError(() => {
        this.snackbar.openSnackBar(
          'Something went wrong! Please contact support.',
          'gtw-snackbar--red',
        );
        return of<DispatchResult>('noop');
      }),
    );
  }

  /**
   * Mirror of `actionMap.downloadBatchAcks.callBack` in
   * `EfileElementsController.js` L428-436: turn the arraybuffer into a
   * Blob and trigger a synthetic click to download the zip.
   */
  private downloadZipAcks(buffer: ArrayBuffer): void {
    try {
      const blob = new Blob([buffer], { type: 'application/zip' });
      const href = URL.createObjectURL(blob);
      const today = new Date();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const yyyy = String(today.getFullYear());
      const link = document.createElement('a');
      link.href = href;
      link.download = `${mm}-${dd}-${yyyy}.zip`;
      link.dispatchEvent(new MouseEvent('click'));
      // Revoke on next tick so the click has already triggered the save.
      setTimeout(() => URL.revokeObjectURL(href), 0);
    } catch (err) {
      console.error('[EfileCtrlFnDispatcher] Failed to save ack zip', err);
    }
  }

  private runSubmissionClobAction(
    rows: any[],
    method: 'disableDuplicateSno' | 'resolveScrubbingsForSub',
    successMessage: string,
  ): Observable<DispatchResult> {
    const { submission, baseUrl, ssoId } = this.context();
    if (!submission?.SUBMISSION_ID) {
      this.snackbar.openSnackBar('No active submission selected', 'gtw-snackbar--red');
      return of<DispatchResult>('noop');
    }

    const clobData = JSON.stringify(rows.map((r: any) => r?.data ?? r));
    const payload = {
      submission_id: submission.SUBMISSION_ID,
      clob_data: clobData,
      sso_id: ssoId,
    };

    const request$ =
      method === 'disableDuplicateSno'
        ? this.actions.disableDuplicateSno(baseUrl, payload)
        : this.actions.resolveScrubbingsForSub(baseUrl, payload);

    return request$.pipe(
      map((response: any) => {
        if (String(response?.callSuccess) === '1') {
          this.snackbar.openSnackBar(successMessage, 'gtw-snackbar--green');
          return 'refresh' as DispatchResult;
        }
        this.snackbar.openSnackBar(
          response?.errorMessage || 'Something went wrong while saving the data!',
          'gtw-snackbar--red',
        );
        return 'noop' as DispatchResult;
      }),
      catchError(() => {
        this.snackbar.openSnackBar('Something went wrong. Please try again.', 'gtw-snackbar--red');
        return of<DispatchResult>('noop');
      }),
    );
  }

  /**
   * Legacy `ModalFactory.getModal('xml2ElementsCompare')` + CE host dialog — same as
   * `GridDirective.handleFormCallback` after `/getScreenModal`.
   */
  private openXmlVsElementsViaGetScreenModal(event: EfileCtrlFnEvent): Observable<DispatchResult> {
    const gridPayload = event?.data ?? {};

    return this.modalFactory.getModal(XML_VS_ELEMENTS_SCREEN_MODAL_NAME, gridPayload).pipe(
      switchMap((response: any) => {
        if (!response || String(response.callSuccess) !== '1') {
          this.snackbar.openSnackBar(
            response?.errorMessage || 'Unable to open the modal. Please try again.',
            'gtw-snackbar--red',
          );
          return of<DispatchResult>('noop');
        }

        const serverCe = response?.modalScreen?.ce_tag_name;
        const entry =
          this.componentRegistry.resolve(XML_VS_ELEMENTS_SCREEN_MODAL_NAME) ||
          this.componentRegistry.resolve('xml2elementscompare');
        const ceTag = (serverCe || entry?.ceTagName) as string | undefined;
        if (!ceTag) {
          this.snackbar.openSnackBar('Unable to resolve modal component.', 'gtw-snackbar--red');
          return of<DispatchResult>('noop');
        }

        const lock = this.buildScreenModalLockPayload(response);
        const dialogData = this.buildCeHostDialogPayload(
          ceTag,
          gridPayload,
          XML_VS_ELEMENTS_SCREEN_MODAL_NAME,
          response,
          entry,
          lock,
        );
        const width = this.resolveCeDialogWidth(
          response?.modalScreen?.modal_size,
          entry?.dialogWidth,
        );
        const panelClass = this.normalizeDialogPanelClass(entry?.dialogPanelClass);

        return new Observable<DispatchResult>((subscriber) => {
          const dialogRef = this.dialog.open(CeHostDialogComponent, {
            data: dialogData,
            width,
            panelClass,
            disableClose: true,
          });
          dialogRef.afterClosed().subscribe({
            next: (result: any) => {
              subscriber.next(result?.action === 'refresh' ? 'refresh' : 'noop');
              subscriber.complete();
            },
            error: () => {
              subscriber.next('noop');
              subscriber.complete();
            },
          });
        });
      }),
      catchError((err) => {
        console.error('[EfileCtrlFnDispatcher] getScreenModal / xml vs elements', err);
        this.snackbar.openSnackBar('Unable to open the modal. Please try again.', 'gtw-snackbar--red');
        return of<DispatchResult>('noop');
      }),
    );
  }

  /** Same contract as `GridDirective` buildLockPayload. */
  private buildScreenModalLockPayload(response: any): ModalLockPayload | undefined {
    const checkRequired = response?.modalScreen?.lock_check_required === 'Y';
    const isLocked = response?.lockResult?.is_locked === 'Y';
    if (!checkRequired || !isLocked) {
      return undefined;
    }
    return {
      isLocked: true,
      checkRequired: true,
      message: response?.lockResult?.lock_message || '',
    };
  }

  /** Same mapping as `GridDirective.resolveDialogWidth`. */
  private resolveCeDialogWidth(modalSize?: string, fallback?: string): string {
    const fb = fallback ?? '90vw';
    if (!modalSize) return fb;
    const s = String(modalSize).trim().toLowerCase();
    switch (s) {
      case 'sm':
        return '350px';
      case 'md':
        return '70vw';
      case 'lg':
        return '92vw';
      case 'xl':
        return '96vw';
    }
    if (/^\d+(\.\d+)?(px|%|vw|vh|em|rem)$/.test(s)) return s;
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    return fb;
  }

  private normalizeDialogPanelClass(basePanel?: string | string[]): string[] {
    const raw = basePanel ?? 'ce-host-dialog-panel';
    const rawPanels: string[] = Array.isArray(raw) ? [...raw] : [String(raw)];
    const out: string[] = [];
    for (const token of rawPanels) {
      for (const part of String(token).trim().split(/\s+/)) {
        if (part) {
          out.push(part);
        }
      }
    }
    return out;
  }

  /**
   * Build the `CeHostDialogData` payload for the return-review
   * `xml2ElementsCompare` flow. Delegates to
   * {@link buildEfileCeHostDialogData} so the dispatcher,
   * `efile-grid.directive`, and `XmlVsElementsComponent` all share the
   * same filterParams + headerData rules — including the per-callName
   * deriver (today: inject `irs_id` from the clicked row when the active
   * tab filter is empty).
   *
   * No `gridExtraParams` / `fallbackHeader`: the dispatcher path runs
   * inside the workspace shell, so `activeScreen.filters` and
   * `activeScreen.data.header` carry the context already.
   */
  private buildCeHostDialogPayload(
    ceTagName: string,
    gridPayload: any,
    callName: string,
    screenModalResponse: any,
    registryEntry: { templateMode?: string; dialogWidth?: string } | undefined,
    lock: ModalLockPayload | undefined,
  ): CeHostDialogData {
    return buildEfileCeHostDialogData({
      ceTagName,
      callName,
      gridPayload,
      workspaceService: this.workspaceService,
      globalService: this.globalService,
      appService: this.appService,
      modalDesc: screenModalResponse?.modalScreen?.modal_desc,
      lock,
      templateMode: registryEntry?.templateMode,
    });
  }
}
